import { useState } from 'react';
import { ChevronDown, FilePlus2, Loader2, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { useListContext } from '@/features/lists/context';
import { listQueries } from '@/features/lists/lists.queries';
import { TemplateSelector } from '@/features/lists/components/TemplateSelector';
import { createListEvent } from '@certhon/models';

export interface AddGroupMenuProps {
  expandTargetNodeId: string;
  onAddGroup: () => void;
}

export function AddGroupMenu({
  expandTargetNodeId,
  onAddGroup,
}: AddGroupMenuProps) {
  const { addEvent, list, user, injectLists } = useListContext();
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expanding, setExpanding] = useState(false);

  const handlePickTemplate = async (templateId: string | null) => {
    if (!templateId) {
      setPickerOpen(false);
      return;
    }
    if (expanding) return;

    setExpanding(true);
    try {
      const fetched = await queryClient.fetchQuery(
        listQueries.detail(templateId),
      );
      if (!fetched || fetched.length === 0) {
        throw new Error('Template returned no data');
      }
      injectLists(fetched);

      addEvent(
        createListEvent('EXPAND_PROTO', {
          user_id: user.recnum,
          list_id: list.id,
          payload: {
            targetNodeId: expandTargetNodeId,
            protoListId: templateId,
          },
        }),
      );
      setPickerOpen(false);
    } catch (error) {
      toast.error('Could not load template', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setExpanding(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (expanding && !next) return;
    setPickerOpen(next);
  };

  return (
    <Popover open={pickerOpen} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1 px-2 text-xs"
            onClick={onAddGroup}
            disabled={expanding}
          >
            <Plus className="h-3.5 w-3.5" />
            Add group
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1 px-2 text-xs"
            onClick={() => setPickerOpen(true)}
            disabled={expanding}
          >
            {expanding ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FilePlus2 className="mr-2 h-4 w-4" />
            )}
            Expand template…
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="More add-group options"
                className="text-muted-foreground hover:text-foreground h-7 w-7"
                disabled={expanding}
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onSelect={() => onAddGroup()}>
                <Plus className="mr-2 h-4 w-4" />
                Add group
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setPickerOpen(true)}>
                <FilePlus2 className="mr-2 h-4 w-4" />
                Expand template…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side="top"
        sideOffset={8}
        className="w-72 p-2"
      >
        <TemplateSelector
          value={null}
          onChange={handlePickTemplate}
          placeholder="Select a template to expand…"
          noneLabel="Cancel"
          disabled={expanding}
        />
        {expanding && (
          <div className="text-muted-foreground mt-2 flex items-center gap-2 text-xs">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading template…
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
